import { useAtom, useAtomValue, useSetAtom, type Atom, type WritableAtom } from 'jotai';
import { useEffect } from 'react';
import { loadable, unwrap } from 'jotai/utils';
import type { Loadable } from 'jotai/vanilla/utils/loadable';

export function useAsyncAtomValue<T, R extends Awaited<T> | undefined = undefined>(
    atom: Atom<T>,
    defaultValue?: R,
): R extends undefined ? Awaited<T> | undefined : Awaited<T> {
    const loadableValue: Loadable<T> = useAtomValue(loadable(atom));
    if (loadableValue.state === 'hasData') {
        return loadableValue.data;
    }
    return defaultValue as Awaited<T>;
}

export function useLoadableAtomValue<T, R extends Awaited<T> | undefined = undefined>(
    atom: Atom<T>,
    defaultValue?: R,
): [R extends undefined ? Awaited<T> | undefined : Awaited<T>, Loadable<T>] {
    const loadableValue: Loadable<T> = useAtomValue(loadable(atom));
    if (loadableValue.state === 'hasData') {
        return [loadableValue.data, loadableValue] as const;
    }
    return [defaultValue as Awaited<T>, loadableValue] as const;
}

/**
 * `unwrap` memoizes on the (atom, fallback) PAIR — this shared, stable fallback is what keeps the
 * unwrapped atom's identity stable across renders and callers. Never pass an inline arrow to
 * `unwrap` in render: each render would mint a fresh pending atom that renders the fallback forever.
 */
const keepPrevious = <T>(prev?: T): T | undefined => prev;

/**
 * Read an async atom without ever suspending: keeps the last-resolved value while the atom
 * recomputes (e.g. a Firestore listener emitting after a write), instead of bouncing the tree
 * through the nearest <Suspense>. Returns undefined until the first resolve — default at the
 * call site (`?? []`) when the consumer needs a value immediately.
 */
export function useUnwrappedAtomValue<Value>(anAtom: Atom<Value>): Awaited<Value> | undefined {
    return useAtomValue(unwrap(anAtom, keepPrevious));
}

/** Read+write flavor of useUnwrappedAtomValue — writes pass through to the wrapped atom. */
export function useUnwrappedAtom<Value, Args extends unknown[], Result>(anAtom: WritableAtom<Value, Args, Result>) {
    return useAtom(unwrap(anAtom, keepPrevious));
}

/**
 * Read a one-shot `atomWithRefresh` atom that should be current on every visit, such as server counts,
 * and that resolves `undefined` when its read fails. Keeps the last value while recomputing, and marks
 * the atom stale as the reader unmounts, so the next mount refetches instead of serving numbers from the
 * first visit. `failed` is true once a read has settled on `undefined`.
 */
export function useFreshOnMountAtomValue<Value>(anAtom: WritableAtom<Promise<Value | undefined>, [], void>): {
    value: Value | undefined;
    failed: boolean;
} {
    // Order matters: cleanups run in declaration order, so subscribing first makes the refresh land after the
    // unsubscribe, where it only marks the atom stale. Registered first, it would refetch on the way out.
    const value = useUnwrappedAtomValue(anAtom);
    const settled = useAtomLoadable(anAtom).state === 'hasData';
    const refresh = useSetAtom(anAtom);
    useEffect(() => refresh, [refresh]);
    return { value, failed: settled && value === undefined };
}

/**
 * The loadable alone, for a read-only async atom — the read-side twin of `useLoadableSetAtom`, and
 * what a caller reaches for once its writes go through a DAO instead of the atom.
 */
export function useAtomLoadable<T>(atom: Atom<T>): Loadable<T> {
    return useAtomValue(loadable(atom));
}

export function useLoadableSetAtom<Value, Args extends unknown[], Result>(atom: WritableAtom<Value, Args, Result>) {
    const loadableAtom = useAtomValue(loadable(atom));
    const atomSetter = useSetAtom(atom);
    return [loadableAtom, atomSetter] as const;
}
